/**
 * /eg2026, /eg2026/e1, /eg2026/e2 — Dashboards editoriales.
 * E2 simplificada: CTA principal al portal oficial completo (verdadelectoral.controlactas.com)
 * + mini-resumen del subset SAAE marcado explícitamente como parcial.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("es-PE").format(n);
const pct = (n: number | null | undefined, d = 2) =>
  n === null || n === undefined ? "—" : `${n.toFixed(d)}%`;

/* ============================================================== */
/* Índice /eg2026                                                  */
/* ============================================================== */

const SUBPROCESOS = [
  {
    href: "https://verdadelectoral.controlactas.com/saae/online",
    titulo: "Primera vuelta — E1",
    fecha: "12 de abril de 2026",
    detalle:
      "Resultados oficiales ONPE de los 36 candidatos presidenciales. Universo: 92,766 mesas · 27.3 M electores. Cobertura completa del proceso + análisis SAAE en línea.",
    cta: "Ir a SAAE en Línea ↗",
    external: true,
  },
  {
    href: "https://verdadelectoral.controlactas.com/e2/onpe-online/",
    titulo: "Segunda vuelta — E2",
    fecha: "7 de junio de 2026",
    detalle:
      "Resultados oficiales ONPE de la segunda vuelta entre Keiko Fujimori (FP) y Roberto Sánchez (JP). Comportamiento por circunscripción, mesas 900K, encuestas (DATUM e IPSOS) y snapshots de JNE Fiscaliza.",
    cta: "Ir a ONPE en Línea ↗",
    external: true,
  },
];

export function Eg2026IndexPage() {
  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-reconocido">🎯 Elecciones Generales 2026</span>
        <span className="chip-probado">🟢 Datos oficiales ONPE</span>
      </div>
      <h1 className="mt-6 font-serif text-4xl md:text-5xl">Elecciones Generales 2026</h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-brand-navy/70">
        Proceso completo · Primera vuelta + Segunda vuelta
      </p>
      <p className="mt-4 max-w-3xl text-lg text-slate-700">
        Resumen ciudadano del proceso electoral EG2026. La data oficial corre en los servidores
        operativos del ecosistema y aquí se publica con cuidado editorial: cifras oficiales,
        fuentes verificables, semáforos epistemológicos y disclaimers de actualización.
      </p>

      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {SUBPROCESOS.map((s) => (
          <a
            key={s.href}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            className="card-ink group block"
          >
            <div className="text-xs uppercase tracking-widest text-brand-navy/70 font-semibold">
              {s.fecha}
            </div>
            <h2 className="mt-2 font-serif text-2xl group-hover:text-brand-red">{s.titulo}</h2>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">{s.detalle}</p>
            <span className="mt-3 inline-block text-sm font-semibold text-brand-red">{s.cta}</span>
          </a>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Los dashboards oficiales abren en pestaña nueva en el portal hermano del ecosistema
        (verdadelectoral.controlactas.com). Pueden tener identidad visual distinta.
      </p>
    </div>
  );
}

/* ============================================================== */
/* /eg2026/e1                                                      */
/* ============================================================== */

export function Eg2026E1Page() {
  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-reconocido">🎯 Primera vuelta · 12-abr-2026</span>
        <span className="chip-probado">🟢 Snapshot ONPE 16-may-2026</span>
      </div>
      <h1 className="mt-6 font-serif text-4xl md:text-5xl">Primera vuelta presidencial</h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-brand-navy/70">
        36 candidatos + voto en blanco + voto nulo · universo 92,766 mesas
      </p>
      <p className="mt-4 max-w-3xl text-lg text-slate-700">
        El proceso de primera vuelta cerró con tres candidatos en empate técnico (KFH, RS, RLA) y
        un fallo logístico del 12-abr que afectó 256 locales, 3,605 mesas y 55,261 electores
        hábiles que no pudieron votar — investigado y formalmente atribuido a 8 funcionarios de la
        ONPE por la Contraloría General de la República en su{" "}
        <Link to="/caso-galaga" className="text-brand-red font-semibold hover:underline">
          Informe 8536-2026
        </Link>
        .
      </p>

      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-slate-500">
          Lo que ya está disponible en el sitio
        </h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <Link to="/candidatos" className="card-ink group">
            <div className="font-serif text-lg group-hover:text-brand-red">
              Universo de 38 filas — auditoría candidatos →
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Los 36 candidatos presidenciales con cifras oficiales ONPE + voto en blanco + voto
              nulo. Cada uno con ficha individual, plancha completa (presidente y dos
              vicepresidentes) y plan de gobierno (PDF descargable).
            </p>
          </Link>
          <Link to="/caso-galaga" className="card-ink group">
            <div className="font-serif text-lg group-hover:text-brand-red">
              Caso Gálaga — CGR 8536-2026 →
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Informe completo de la Contraloría sobre la contratación del servicio logístico.
              Responsabilidad penal contra 8 funcionarios identificados con DNI.
            </p>
          </Link>
          <Link to="/mesas-900k" className="card-ink group">
            <div className="font-serif text-lg group-hover:text-brand-red">
              Mesas 900K — investigación CCPP →
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Análisis de las 4,703 mesas de centros poblados rurales. Argumento legal Silva
              Huallanca (Ley 28.440). Cruce con crecimiento del padrón distrital.
            </p>
          </Link>
          <Link to="/laboratorio/crecimiento-padron" className="card-ink group">
            <div className="font-serif text-lg group-hover:text-brand-red">
              Crecimiento del padrón distrital 2011-2026 →
            </div>
            <p className="mt-1 text-sm text-slate-700">
              701 distritos con crecimiento extremo (&gt;50%) en 15 años. Base para análisis
              demográfico cruzado con INEI (pendiente).
            </p>
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ============================================================== */
/* /eg2026/e2 — Simplificada: CTA principal + mini-resumen SAAE    */
/* ============================================================== */

type ResumenE2 = {
  meta: { fuente: string; disclaimer: string; cacheado: string };
  data: {
    actas: {
      total_actas: number;
      por_estado: Record<string, number>;
      contabilizadas_con_fecha: number;
    };
    nacional: {
      kf: number;
      rs: number;
      blanco: number;
      nulos: number;
      validos: number;
      pctValidoKf: number;
      pctValidoRs: number;
      ganadorParcial: "KF" | "RS" | "empate";
      deltaVotos: number;
    };
  };
};

const UNIVERSO_TOTAL_EG2026 = 92_766;

export function Eg2026E2Page() {
  const resumen = useQuery({
    queryKey: ["eg2026-e2-resumen"],
    queryFn: async () => (await api.get<ResumenE2>("/eg2026/e2/resumen")).data,
    refetchInterval: 60_000,
  });

  const nac = resumen.data?.data.nacional;
  const actas = resumen.data?.data.actas;
  const pctSaaeDelTotal = actas
    ? (actas.total_actas / UNIVERSO_TOTAL_EG2026) * 100
    : 0;

  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-reconocido">🎯 Segunda vuelta · 7-jun-2026</span>
        <span className="chip-probado">🟢 Resultados ONPE en curso</span>
      </div>
      <h1 className="mt-6 font-serif text-4xl md:text-5xl">Segunda vuelta presidencial</h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-brand-navy/70">
        Keiko Fujimori (FP) vs Roberto Sánchez (JP)
      </p>

      {/* CTA PRINCIPAL al dashboard oficial completo */}
      <section className="mt-8 rounded-lg border-2 border-brand-red bg-brand-red-50 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="text-xs uppercase tracking-widest text-brand-red font-bold">
              ★ Conteo oficial completo
            </div>
            <h2 className="mt-1 font-serif text-2xl md:text-3xl">
              ONPE en Línea · Segunda vuelta presidencial EG2026
            </h2>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              Para el <strong>conteo oficial completo</strong> de la segunda vuelta —resultado
              nacional minuto a minuto, mapa por circunscripción, búsqueda por mesa y trazabilidad
              acta por acta— consulta el dashboard ONPE en Línea en el portal hermano del
              ecosistema:
            </p>
          </div>
          <a
            href="https://verdadelectoral.controlactas.com/e2/onpe-online/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-brand-red text-white px-6 py-3 text-base font-bold hover:bg-brand-red-700 transition shadow-lg whitespace-nowrap"
          >
            Ir a ONPE en Línea →
          </a>
        </div>
        <a
          href="https://verdadelectoral.controlactas.com/e2/encuestadoras"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex w-full items-center justify-between rounded-md bg-white border-2 border-brand-navy/30 text-brand-navy px-4 py-3 hover:border-brand-navy hover:shadow-sm transition text-sm font-semibold"
        >
          <span>Encuestadoras · comparativa DATUM e IPSOS boca urna + conteo rápido</span>
          <span className="text-brand-red text-lg">→</span>
        </a>
        <p className="mt-3 text-xs text-slate-600">
          <strong>Nota:</strong> el portal hermano usa otra identidad visual (tema oscuro
          dorado/crimson). Abre en pestaña nueva.
        </p>
      </section>

      {/* Mini-resumen SAAE · explícitamente marcado como SUBSET parcial */}
      {nac && actas && (
        <section className="mt-10">
          <div className="flex items-center gap-2">
            <span className="chip-vacio">⚫ Subset SAAE</span>
            <h2 className="text-xs uppercase tracking-widest text-slate-500">
              Snapshot del scraping interno SAAE (referencia editorial)
            </h2>
          </div>
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <strong className="text-brand-navy">⚠ Cifras parciales.</strong> El sistema operativo
            SAAE en PRESI-NEW ha capturado <strong>{fmt(actas.total_actas)}</strong> actas (de
            las <strong>{fmt(UNIVERSO_TOTAL_EG2026)}</strong> totales del proceso ·{" "}
            {pct(pctSaaeDelTotal, 2)} del universo). El conteo oficial completo está en{" "}
            <a
              href="https://verdadelectoral.controlactas.com/e2/onpe-online/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-red font-semibold hover:underline"
            >
              ONPE en Línea ↗
            </a>
            . Lo de abajo es solo el subset SAAE.
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <SubsetKpi label="Actas en SAAE" value={fmt(actas.total_actas)} hint={`${pct(pctSaaeDelTotal, 2)} del universo total`} />
            <SubsetKpi label="Contabilizadas" value={fmt(actas.por_estado["C"])} />
            <SubsetKpi label="KF en SAAE" value={fmt(nac.kf)} hint={`${pct(nac.pctValidoKf)} sobre válidos parciales`} />
            <SubsetKpi label="RS en SAAE" value={fmt(nac.rs)} hint={`${pct(nac.pctValidoRs)} sobre válidos parciales`} />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Snapshot cacheado 5 min · auto-refresh 60 s · fuente:{" "}
            {resumen.data?.meta.fuente}
          </p>
        </section>
      )}

      {/* Información complementaria del sitio */}
      <section className="mt-12">
        <h2 className="text-xs uppercase tracking-widest text-slate-500">
          Análisis complementario en este sitio
        </h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <Link to="/mesas-900k" className="card-ink group">
            <div className="font-serif text-lg group-hover:text-brand-red">
              Mesas 900K — comportamiento histórico →
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Las 4,703 mesas de centros poblados rurales muestran un patrón distinto al promedio
              nacional. Análisis de serie 2006-2026 + cruce con crecimiento del padrón distrital.
            </p>
          </Link>
          <Link to="/caso-galaga" className="card-ink group">
            <div className="font-serif text-lg group-hover:text-brand-red">
              Caso Gálaga (E1) — contexto del proceso →
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Informe CGR 8536-2026: 8 funcionarios identificados con DNI, recomendación de
              acciones penales por el fallo logístico del 12-abr en primera vuelta.
            </p>
          </Link>
        </div>
      </section>
    </div>
  );
}

function SubsetKpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-xl tabular-nums text-brand-navy">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}
